import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigation, useParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, styled, IconButton, Skeleton, CircularProgress } from "@mui/material";
import { ReactComponent as ClientActivity } from "assets/catalogSetting/activity.svg";
import { ReactComponent as ClientActivityReport } from "assets/catalogSetting/report.svg";
import { ReactComponent as ClientActivityReportNew } from "assets/icons/Blue_ClientReport.svg";
import { ReactComponent as ClientActivityNew } from "assets/icons/Yellow_ ClientActivity.svg";
import { ReactComponent as CatalogReshareNew } from "assets/icons/Green_ReshareIcon.svg";
import { ReactComponent as EditNewIcon } from "assets/icons/White_editicon.svg";

import { ReactComponent as ClientActivityEdit } from "assets/catalogSetting/edit.svg";
import { ReactComponent as CatalogReshare } from "assets/catalogSetting/reshare.svg";
import { ReactComponent as Background } from "assets/icons/bg.svg";
import { useCatalogSettingContext } from "context/CatalogSettingContext";
// import CatalogueShareLinkModule from "./CatalogueShareLinkModule";
import UploadExcelModule from "./UploadExcelModal";
import CatalogueAddAndEditClientDetails from "./CatalogueAddAndEditClientDetails";
import { useGetAllCatalogsClients } from "../hooks/useGetAllCatalogsClients";
import { useCreateCatalogClient } from "../hooks/useCreateCatalogClient";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";
import ClinetTableRowSkeleton from "../utilis/ClinetTableRowSkeleton";
import { useUpdateCatalogClient } from "../hooks/useUpdateCatalogClient";
import { covertingDateAndTime } from "../utilis/covertingDateAndTime";
import { usePermissions } from "context/PermissionsContext";
import { findSpecificMenu, findSpecificPage, findSpecificSubMenu, findSpecificSubPage, hasAccessPage, hasAccessSubPage, isAllowed } from "utils/checkUserRigths";
import { capitalEachLetter } from "utils/capitalEachLetter";
import { useUploadBulkClients } from "../hooks/useUploadBulkClients";
import apiClient from "lib/api-client";
import { toast as myToast } from "react-toastify";
import { formatDate, formatDesignDate } from "utils/formatDate";
import { formatTime } from "utils/formatTime";
import { useGetAllCatalogLinkData, useGetAllCatalogSharedData } from "../hooks/useGetAllCatalogSharedData";
import Tooltip from "components/ui/Tooltip";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";

const convertingSecondIntoHoursFormat = (sec) => {
  const hours = Math.floor(sec / 3600); // Get the total hours
  const minutes = Math.floor((sec % 3600) / 60); // Get the remaining minutes
  const seconds = sec % 60; // Get the remaining seconds

  // Format the result as HH:MM:SS
  return [
    hours.toString().padStart(2, "0"), // Ensure 2 digits for hours
    minutes.toString().padStart(2, "0"), // Ensure 2 digits for minutes
    seconds.toString().padStart(2, "0"), // Ensure 2 digits for seconds
  ].join(":");
};

const formatSecondsToHHMMSS = (totalSeconds) => {
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};

const getRemainingDurationFromSession = (sessionData) => {
  const { isLogin, sessionLoginDateTime, sessionDuration, sessionCreatedDateTime, status } = sessionData.sessionInfo;

  if (status === "Inactive") {
    return "00:00:00";
  }

  if (!sessionDuration) return "00:00:00"; //sessionDuration

  const totalSeconds = Number(sessionDuration); // 3600

  if (isLogin === false || !sessionLoginDateTime) {
    return formatSecondsToHHMMSS(totalSeconds);
  }

  const loginTime = new Date(sessionLoginDateTime).getTime();
  const now = Date.now();

  const expiryTime = loginTime + totalSeconds * 1000;
  const remainingMs = expiryTime - now;

  if (remainingMs <= 0) return "00:00:00";

  return formatSecondsToHHMMSS(Math.floor(remainingMs / 1000));
};

// Styled component for the entire table with padding
const StyledTableContainer = styled(TableContainer)(({ isLoading }) => ({
  backgroundColor: "transparent",
  maxHeight: "100%",
  minHeight: "100%",
  position: "relative",
  marginBottom: "0px",
  paddingBottom: "5px",
  overflowX: isLoading ? "hidden" : "auto", // Enable horizontal scrolling
  overflowY: "auto", // Optional: Hide vertical scrollbar if not needed
  borderTop: "1px solid #fff",

  "&::-webkit-scrollbar": {
    height: "0.5px !important",
    background: "#000000 !important",
  },
  ".css-jpeuvb-MuiTableCell-root": {
    padding: isLoading ? "8px" : "16px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#000000 !important",
    borderRadius: "8px !important",
  },

  // Scrollbar styles for Firefox
  scrollbarWidth: "thin" /* Thin scrollbar */,
  scrollbarColor: "#444444 #000000" /* Black thumb, white track */,
  scrollBehavior: "smooth",
}));

const StyledTable = styled(Table)(({ isLoading }) => ({
  "& th, & td": {
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "22px",
    color: "#FFFFFF",
    textAlign: "center",
  },
  "& th": {
    backgroundColor: "#444444",
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ isLoading }) => ({
  fontFamily: "Poppins !important",
  fontSize: "14px",
  fontWeight: "400 !important",
  border: "none",
  position: "sticky",
  top: 0,
  zIndex: 1,
  "&:first-child": {
    borderLeft: "1px solid #fff",
  },
  "&:last-child": {
    borderRight: "1px solid #fff",
  },
}));

const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "Karla !important",

  border: "none",
  whiteSpace: "nowrap",
  fontSize: "16px !important",
  // border: "1px solid #fff",
  borderBottom: "1px solid #fff",
  borderLeft: "1px solid #fff",
  borderRight: "1px solid #fff",

  "&:first-child": {
    paddingLeft: "0px",
  },
  "&:last-child": {
    paddingRight: "0px",
  },
}));

const StyledTableRow = styled(TableRow)(({}) => ({
  "&:last-of-type td": {
    padding: "0px",
    margin: "0px",
  },
}));

const CustomIconButton = styled(IconButton)(({ theme }) => ({
  padding: "0px",
  "&.Mui-disabled": {
    color: "lightgray",
    cursor: "not-allowed",
    opacity: 0.2,
  },
}));

const CatalogLinkUserNew = () => {
  const { id } = useParams();
  const navigation = useNavigate();
  let location = useLocation();

  const [reshareModal, setReshareModal] = useState(false);
  const [reshareData, setReshareData] = useState({});

  // const [userShareLinkData, setUserShareLinkData] = useState();
  const [reshareLoading, setReshareLoading] = useState(null);
  const [userShareLinkData, setUserShareLinkData] = useState(JSON.parse(sessionStorage.getItem("userShareLinkData")) || []);
  const { shareLinkOpen, setShareLinkOpen, uploadDataModal, setUploadDataModal, updateClient, setUpdateClient, isEditClinet, setIsEditClinet, activeTable, setActiveTable } =
    useCatalogSettingContext();

  const [activeTab, setActiveTab] = useState(activeTable);
  const isMyBinnysApp = activeTab === "My Binny's";
  const isLinksTab = activeTab === "Links";

  const { ref, inView } = useInView({
    threshold: 1.0,
  });

  const permission = usePermissions();
  const hasViewMyBinnysAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Catalog Access Management"), "my_binny's_management");
  const hasViewMySessionAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Miscellaneous Catalog"), "session_history");
  const hasViewReshareAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Miscellaneous Catalog"), "reshare_link");
  const hasViewEditAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Miscellaneous Catalog"), "edit_mobile_number_of_links");

  const hasViewLinkAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Catalog Access Management"), "link_management");
  // const hasViewClientReport = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Catalog Setting", permission), "Client Report"), "view");
  const hasViewClientReport = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Reports"), "client_reports");

  // const hasViewClientActivity = hasAccessPage(findSpecificPage("Client Activity", permission), "view");
  // const hasEditClientMobileNumber = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Catalog Setting", permission), "Add Mobile No."), "edit");
  // const hasReshareCatalogLink = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Catalog", permission), "Share Catalog"), "view");
  // const hasReshareCatalogLinkAdd = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Catalog", permission), "Share Catalog"), "add");
  const hasViewClientActivity = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Miscellaneous Catalog"), "session_history");

  const {
    data: myBinnysData,
    isLoading: myBinnysLoading,
    fetchNextPage: fetchMyBinnysNextPage,
    hasNextPage: hasMyBinnysNextPage,
    isError: myBinnysError,
    isFetching: isMyBinnysFetching,
    isFetchingNextPage: isMyBinnysFetchingNext,
    error: myBinnysErrorObj,
  } = useGetAllCatalogSharedData(
    { catalogId: id },
    {
      enabled: isMyBinnysApp && hasViewMyBinnysAccess,
    }
  );

  const {
    data: linksData,
    isLoading: linksLoading,
    fetchNextPage: fetchLinksNextPage,
    hasNextPage: hasLinksNextPage,
    isError: linksError,
    isFetching: isLinksFetching,
    isFetchingNextPage: isLinksFetchingNext,
    error: linksErrorObj,
  } = useGetAllCatalogLinkData(
    { catalogId: id },
    {
      enabled: isLinksTab && hasViewLinkAccess,
    }
  );

  const tableData = isMyBinnysApp ? myBinnysData : linksData;
  const isLoading = isMyBinnysApp ? myBinnysLoading : linksLoading;
  const isFetchingNextPage = isMyBinnysApp ? isMyBinnysFetchingNext : isLinksFetchingNext;
  const hasNextPage = isMyBinnysApp ? hasMyBinnysNextPage : hasLinksNextPage;
  const fetchNextPage = isMyBinnysApp ? fetchMyBinnysNextPage : fetchLinksNextPage;
  const error = isMyBinnysApp ? myBinnysErrorObj : linksErrorObj;

  const { mutate: createCatalogClient, isError, isSuccess, error: addError, isPending: isClientLoading } = useCreateCatalogClient();
  const { mutate: editCatalogClient, isError: editError, isSuccess: editSuccess, error: updateError, isPending: isEditClientLoading } = useUpdateCatalogClient();

  //for the bulk upload clients custom hook
  const { mutate: uploadClients } = useUploadBulkClients();

  const handleClientActivityScreen = (curData, catClientId, companyName) => {
    let phoneNumber = curData?.mobileNumber || curData?.clientNumber;
    let catalogClientId = catClientId || curData?._id;
    let catalogNumber = location?.state?.catalogName[0]?.match(/\d+/)?.[0];

    let subName = companyName ? `- ${companyName}` : `- ${phoneNumber?.slice(3)?.trim()} (${curData?.clientName})`;

    location = {
      ...location,
      state: {
        ...location.state,
        catalogId: id,
        subName: subName,
        isSession: true,
        catalogNumber: `#${catalogNumber}`,
      },
    };

    // navigation(`/CatalogSetting/Client-Activity/${id}/${catalogClientId}?companyName=${companyName}`, { ...location });

    const path = companyName ? `/CatalogSetting/Client-Activity/${id}/${catalogClientId}?companyName=${encodeURIComponent(companyName)}` : `/CatalogSetting/Client-Activity/${id}/${catalogClientId}`;
    navigation(path, { ...location });
  };

  const handleMyBinnysClientReport = (curData, catClientId, companyName) => {
    try {
      let phoneNumber = curData?.mobileNumber || curData?.clientNumber;
      let catalogClientId = catClientId || curData?._id;
      let catalogNumber = location?.state?.catalogName[0]?.match(/\d+/)?.[0];

      location = {
        ...location,
        state: {
          ...location.state,
          catalogId: id,
          subName: `- ${phoneNumber?.slice(3)?.trim()} (${curData?.clientName})`,
          isClientReport: true,
          catalogNumber: `#${catalogNumber}`,
        },
      };

      navigation(`/CatalogSetting/Client-Report/${id}/${catClientId}?companyName=${companyName}`, { ...location });

      // navigation(`/CatalogSetting/Client-Activity/${id}/${catalogClientId}`, { ...location });
    } catch (error) {
      alert("handleMyBinnysClientReport----->", error);
    }
  };

  const handleClientReportScreen = (curData) => {
    location = {
      ...location,
      state: {
        ...location.state,
        catalogId: id,
        subName: `- ${curData?.mobileNumber?.slice(3)?.trim()} (${curData?.clientName})`,
      },
    };
    navigation(`/CatalogSetting/Client-Report/${id}/${curData._id}`, { ...location });
  };

  const handleNewAccesUserAdd = (newUserData) => {
    let newData = {
      catalogId: id,
      clientNumber: newUserData.mobileNumber,
      clientName: newUserData.clientName,
      clientEmail: newUserData.clientEmail,
      sessionDuration: newUserData.hours,
      bookType: newUserData.bookType,
    };
    createCatalogClient(newData);
    // if (!error) {
    //   setShareLinkOpen(false);
    // }
  };

  const handleAccesUserEdit = (editUserData) => {
    let updateClient = {
      catalogClinetId: editUserData?._id,
      clientNumber: editUserData?.mobileNumber,
      clientName: editUserData?.clientName,
    };

    editCatalogClient(updateClient);
    // setShareLinkOpen(false);
    // setUpdateClient({});
  };



  const handleOpenReshare = (clientName, clientNumber) => {
    setReshareData({ clientName, clientNumber });
    setReshareModal(true);
  };

  const handleReshareCatalog = async (clientName, clientNumber) => {
    if (reshareLoading === clientNumber) return;
    setReshareLoading(clientNumber);

    try {
      const payload = {
        catalogId: id,
        clientNumber,
      };

      const response = await apiClient.post("/api/v1/catalogSetting/catalog-client/reshare", payload);

      if (response.status !== 200) {
        throw new Error("Error resharing catalog");
      }

      // myToast.success(response.data.message);
      setReshareModal(false);
      myToast.success(`Catalog Link Reshared Successfully to ${capitalEachLetter(clientName)}`);
    } catch (error) {
      console.error("Reshare catalog error:", error);
      // alert("Sahil");
      myToast.error(error?.response?.data?.message || error?.message || "Failed to Reshared Link");
    } finally {
      setReshareLoading(null);
    }
  };

  const fetchNextPageData = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (inView) {
      fetchNextPageData();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (isError || editError) {
      setShareLinkOpen(true);
    } else {
      setShareLinkOpen(false);
    }
  }, [isError, editError]);

  const handleBulkUploadAccesUser = async (data) => {
    const { fileData } = data;
    if (!fileData || !id) {
      toast.error(`Something went wrong`, {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }
    uploadClients({ file: fileData, catalogId: id });
  };

  const TABLE_CONFIG = {
    "My Binny's": {
      headers: [
        { key: "companyName", label: "Company Name", minWidth: 180 },
        { key: "status", label: "Status", minWidth: 180 },
        { key: "durationRemain", label: "Duration Remain", minWidth: 180 },
        { key: "sharedBy", label: "Shared By", minWidth: 180 },
        { key: "sharedTime", label: "Shared Time", minWidth: 220 },
        { key: "actions", label: "Actions", minWidth: 200 },
      ],
    },

    Links: {
      headers: [
        { key: "clientName", label: "Client Name", minWidth: 160 },
        { key: "mobileNumber", label: "Mobile No.", minWidth: 150 },
        { key: "email", label: "Email", minWidth: 140 },
        { key: "firstLogIn", label: "First Log-in", minWidth: 160 },
        { key: "noOfLogIn", label: "No. of Log-in", minWidth: 100 },
        { key: "sessionInfo", label: "Duration Remain", minWidth: 110 },
        { key: "sharedBy", label: "Shared By", minWidth: 140 },
        { key: "sharedTime", label: "Shared Time", minWidth: 160 },
        { key: "actions", label: "Actions", minWidth: 240 },
      ],
    },
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setActiveTable(activeTab);
  }, [activeTab]);

  useEffect(() => {
  if (!hasViewMyBinnysAccess && hasViewLinkAccess) {
    setActiveTab("Links");
  }

  if (hasViewMyBinnysAccess && !hasViewLinkAccess) {
    setActiveTab("My Binny's");
  }
}, [hasViewMyBinnysAccess, hasViewLinkAccess]);

  const tableConfig = TABLE_CONFIG[activeTab];

  const isCatalogClinetsPresent = (tableData) => {
    if (tableData && tableData.pages && tableData.pages.length > 0) {
      return tableData.pages.some((page) => page.data && page.data.result.length > 0);
    }
    return false;
  };

  const renderModifiedStatus = (status) => {
    let label = status;
    let color = "#000";

    switch (status) {
      case "Active":
        label = "Opened";
        color = "#50C878";
        break;

      case "Not Opened":
        label = "Unopened";
        color = "#FFD700";
        break;

      case "Expired":
        label = "Expired";
        color = "#E77B7B";
        break;

      default:
        label = status || "-";
        color = "#000";
    }

    return <span style={{ color, fontWeight: 500 }}>{label}</span>;
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "companyName":
        return capitalEachLetter(row.companyName);
      case "clientName":
        return capitalEachLetter(row.clientName);

      case "mobileNumber":
        return row.mobileNumber || "-";

      case "status":
        return renderModifiedStatus(row.status) || "-";

      case "email":
        return row.email || "-";

      case "firstLogIn":
        return row.firstLogIn ? formatDesignDate(row.firstLogIn.loginDate, "-") : "-";

      case "noOfLogIn":
        return row.noOfLogIn > 0 ? row.noOfLogIn : "-";

      case "durationRemain":
        return convertingSecondIntoHoursFormat(Math.floor(row.durationRemain / 1000)) || "00:00:00";

      case "sessionInfo":
        return getRemainingDurationFromSession(row.sessionData) || "00:00:00";

      case "sharedBy":
        return capitalEachLetter(row.sharedBy) || "-";

      case "sharedTime":
        return row.sharedTime ? formatDesignDate(row.sharedTime, "-") : "-";

      case "actions":
        return isMyBinnysApp ? (
          <>
            {hasViewClientReport &&  (
              <Tooltip title="Client Report" arrow>
                <IconButton disableRipple>
                  <ClientActivityReportNew onClick={() => handleMyBinnysClientReport(row?.adminClientData, row?.catalogClientId, row?.companyName)} />
                </IconButton>
              </Tooltip>
            )}
            {hasViewClientActivity && (
              <Tooltip title="Session history" arrow>
                <IconButton disableRipple onClick={() => handleClientActivityScreen(row?.adminClientData, row?.catalogClientId, row?.companyName)}>
                  <ClientActivityNew />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
            {hasViewReshareAccess  && (
              <Tooltip title="Reshare Link" arrow>
                <CustomIconButton disableRipple disabled={row.durationRemain === "00:00:00"} onClick={() => handleOpenReshare(row.clientName, row.mobileNumber)}>
                  <CatalogReshareNew />
                </CustomIconButton>
              </Tooltip>
            )}
            {hasViewClientReport && (
              <Tooltip title="Client Report" arrow>
                <IconButton disableRipple sx={{ p: 0 }} onClick={() => handleClientReportScreen(row)}>
                  <ClientActivityReportNew />
                </IconButton>
              </Tooltip>
            )}
            {(hasViewClientActivity && hasViewMySessionAccess) && (
              <Tooltip title="Session history" arrow>
                <IconButton disableRipple sx={{ p: 0 }} onClick={() => handleClientActivityScreen(row)}>
                  <ClientActivityNew />
                </IconButton>
              </Tooltip>
            )}
            {hasViewEditAccess && (
              <Tooltip title="Edit" arrow>
                <CustomIconButton
                  disableRipple
                  disabled={row.noOfLogIn > 0}
                  onClick={() => {
                    setUpdateClient({
                      ...row,
                      mobileNumber: row?.mobileNumber?.replace("+", "").replace(/ /g, ""),
                    });
                    setIsEditClinet(true);
                    setShareLinkOpen(true);
                  }}
                >
                  <EditNewIcon />
                </CustomIconButton>
              </Tooltip>
            )}
          </div>
        );

      default:
        return "-";
    }
  };

  const reshareModalBtn = [
    {
      btnName: "No",
      bgColor: "#E77B7B",

      click: () => {
        setReshareModal(false);
      },
    },
    {
      btnName: "Yes",
      bgColor: "#50C878",
      click: () => {
        handleReshareCatalog(reshareData?.clientName, reshareData?.clientNumber);
      },
    },
  ];

  if (error) {
    toast.error(`${error}`, {
      position: "top-right",
      autoClose: 2000,
    });
  }

  return (
    <>
      <div>
        {/* tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: "1.2vw", fontFamily: "Poppins", fontSize: "0.8vw", fontWeight: 600, paddingBottom: "28px" }}>
          {Object.keys(TABLE_CONFIG)
            .filter((tab) => {
              if (tab === "My Binny's") return hasViewMyBinnysAccess;
              if (tab === "Links") return hasViewLinkAccess;
              return true;
            })
            .map((tab) => (
              <div
                key={tab}
                style={{ borderBottom: activeTab === tab ? "2px solid #fff" : "", cursor: "pointer", userSelect: "none", color: activeTab === tab ? "#fff" : "#6b6a6aff" }}
                onClick={() => handleTabClick(tab)}
              >
                {tab}
              </div>
            ))}
        </div>
      </div>

      {isLoading ? (
        <StyledTableContainer sx={{ maxHeight: "vh" }} isLoading={isLoading}>
          <StyledTable isLoading={isLoading}>
            <TableHead>
              <TableRow>
                {tableConfig.headers.map((header) => (
                  <StyledTableHeaderCell
                    isLoading={isLoading}
                    key={header.key}
                    sx={{
                      minWidth: header.minWidth,
                      fontSize: header.key === "noOfLogIn" || header.key === "sessionInfo" ? "10px !important" : "14px !important",
                      padding: isMyBinnysApp ? "10px 5px !important" : "10px 5px !important",
                    }}
                  >
                    {header.label}
                  </StyledTableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 7 }, (_, index) => (
                <TableRow key={index}>
                  {tableConfig.headers.map((header) => (
                    <StyledTableBodyCell key={header.key}>
                      <Skeleton variant="text" margin="auto" width="100%" height="auto" />
                    </StyledTableBodyCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      ) : isCatalogClinetsPresent(tableData) ? (
        <div>
          <StyledTableContainer sx={{ maxHeight: "65vh" }}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  {tableConfig.headers.map((header) => (
                    <StyledTableHeaderCell
                      key={header.key}
                      sx={{
                        minWidth: header.minWidth,
                        fontSize: header.key === "noOfLogIn" || header.key === "sessionInfo" ? "10px !important" : "14px !important",
                        padding: isMyBinnysApp ? "10px 5px !important" : "10px 5px !important",
                      }}
                    >
                      {header.label}
                    </StyledTableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableData?.pages?.map((page, pageIndex) => (
                  <React.Fragment key={pageIndex}>
                    {page?.data?.result?.map((row) => (
                      <StyledTableRow key={row._id}>
                        {tableConfig.headers.map((header) => (
                          <StyledTableBodyCell
                            key={header.key}
                            sx={{
                              fontSize:
                                header.key === "sharedTime" || header.key === "firstLogIn"
                                  ? isMyBinnysApp
                                    ? "16px !important"
                                    : "12px !important"
                                  : header.key === "email"
                                    ? "10px !important"
                                    : "16px !important",

                              padding: isMyBinnysApp ? "0px !important" : "10px 5px !important",
                            }}
                          >
                            {renderCell(row, header.key)}
                          </StyledTableBodyCell>
                        ))}
                      </StyledTableRow>
                    ))}
                  </React.Fragment>
                ))}

                {isFetchingNextPage && <ClinetTableRowSkeleton TableRow={TableRow} Skeleton={Skeleton} StyledTableHeaderCell={StyledTableHeaderCell} />}

                <StyledTableRow ref={ref}>
                  <StyledTableBodyCell colSpan={tableConfig.headers.length} />
                </StyledTableRow>
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        </div>
      ) : (
        // </Paper>
        <div
          style={{
            display: "flex",
            textAlign: "center",
            margin: "auto",
            padding: "auto",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <Background />
        </div>
      )}

      {/* {<CatalogueShareLinkModule open={shareLinkOpen} setOpen={setShareLinkOpen} handleRef={handleNewAccesUserAdd} />} */}
      {
        <CatalogueAddAndEditClientDetails
          open={shareLinkOpen}
          setOpen={setShareLinkOpen}
          handleRef={!isEditClinet ? handleNewAccesUserAdd : handleAccesUserEdit}
          isError={isError || editError}
          isSuccess={isSuccess || editSuccess}
          isLoading={isClientLoading || isEditClientLoading}
        />
      }
      {<UploadExcelModule open={uploadDataModal} setOpen={setUploadDataModal} handleRef={handleBulkUploadAccesUser} />}
      <RemoveModal open={reshareModal} setOpen={setReshareModal} btnInfo={reshareModalBtn} heading="Are you sure you want to Re-share Link ?" />
    </>
  );
};

export default CatalogLinkUserNew;
